// Consolidated v2 policy-change-request endpoints.
// These replace the legacy per-policy-type endpoints. The single POST endpoint
// discriminates by `policy_type` in the request body; the GET endpoint accepts
// filter query params (policy_type, customer_policy_id, change_request_code,
// status, assigned_to_id, limit).

// POST — create or update (auth). Discriminates by policy_type in the body.
export const EDIT_POLICY_CHANGE_REQUEST_V2 =
  "/policy/edit-policy-change-request";

// POST — public/no-auth variant (matches the 4 legacy pairs).
export const EDIT_PUBLIC_POLICY_CHANGE_REQUEST_V2 =
  "/policy/edit-public-policy-change-request";

// GET — list with filters.
export const GET_POLICY_CHANGE_REQUEST_V2 =
  "/policy/get-policy-change-request";

// policy_type value for the POST body (edit-policy-change-request).
// NOTE: the POST endpoint expects these short codes — do NOT pass the form's
// display value (e.g. "Business Policy"), which the API rejects.
export const POLICY_CHANGE_V2_TYPE = {
  AUTO: "Automobile Policy",
  BUSINESS: "Business Policy",
  COMMAUTO: "Commercial Auto Policy",
  HABITATIONAL: "Habitational Policy",
} as const;

export type IPolicyChangeV2Type =
  (typeof POLICY_CHANGE_V2_TYPE)[keyof typeof POLICY_CHANGE_V2_TYPE];

// policy_type value for the GET filter (get-policy-change-request).
// NOTE: the GET endpoint expects the full display names (POLICIES enum values),
// which differ from the POST body's short codes above.
export const POLICY_CHANGE_V2_GET_TYPE = {
  AUTO: "Automobile Policy",
  BUSINESS: "Business Policy",
  COMMAUTO: "Commercial Auto Policy",
  HABITATIONAL: "Habitational Policy",
} as const;

export type IPolicyChangeV2GetType =
  (typeof POLICY_CHANGE_V2_GET_TYPE)[keyof typeof POLICY_CHANGE_V2_GET_TYPE];

export type IPolicyChangeV2GetFilters = {
  policy_type?: string;
  customer_policy_id?: number | string;
  change_request_code?: string;
  status?: string;
  assigned_to_id?: string;
  limit?: number;
  from_date?: string;
  to_date?: string;
  advanceFilter?: string;
};

// Server-managed fields returned by GET /policy/get-policy-change-request that
// must NOT be sent back in the POST body (edit-policy-change-request).
export const POLICY_CHANGE_V2_OMIT_ON_POST = [
  "all_policy_change_request_id",
] as const;

// Returns a shallow copy of the record with the server-managed fields removed,
// so they are not echoed back on the v2 POST call.
export const omitPolicyChangeV2Fields = <T extends Record<string, any>>(
  data: T
): T => {
  if (!data) {
    return data;
  }
  const clone: Record<string, any> = { ...data };
  POLICY_CHANGE_V2_OMIT_ON_POST.forEach((field) => {
    delete clone[field];
  });
  return clone as T;
};

// Builds a query string for GET /policy/get-policy-change-request, skipping
// any filter that is null/undefined/empty.
export const buildPolicyChangeV2Query = (
  filters: IPolicyChangeV2GetFilters
): string => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};
